import { z } from 'zod';
import { AgentSessionStateSchema } from './state.js';
import { AgentSessionSummarySchema } from './summary.js';
/**
 * Metadata exposed when an administrator inspects sessions across users.
 *
 * This is not a de-identified view. The title is a label a user wrote, so it can carry anything
 * they typed, and `userId` names the owner. Both are here because a list of opaque identifiers
 * cannot be navigated, and the cost is that listing is audited in the same way reading a session
 * is. The conversation itself still only comes from the message route.
 */
export const AgentSessionAdminSummarySchema = AgentSessionSummarySchema.pick({
    created: true,
    id: true,
    title: true,
    updated: true,
    userId: true,
}).extend({
    state: AgentSessionStateSchema,
});
export const AgentSessionAdminSummariesSchema = z.array(AgentSessionAdminSummarySchema);
export const AgentSessionAdminSortFieldSchema = z.enum(['created', 'updated']);
/**
 * How an administrator narrows the cross-user session list.
 *
 * `sessionId` matches whole identifiers only. The column is the primary key, so an exact match
 * is a lookup, where a partial one would scan every session in the deployment. It names one
 * session outright, so `states` is not applied alongside it: an administrator holding the id of
 * a soft-deleted conversation should not have to guess at a second filter to see it.
 *
 * `states` otherwise defaults to active sessions alone, so a soft-deleted conversation stays out
 * of a list until someone asks for it. A single state may be sent unbracketed: Express parses
 * `states=SoftDeleted` as a string rather than a one-element array.
 *
 * The `Before` bounds are inclusive, so a whole day can be selected by naming its start and end.
 *
 * `title` matches any part of a name, case-insensitively, because a viewer searching a name
 * remembers a word from it rather than the whole label. Unlike `sessionId` this cannot use an
 * index, which is affordable at administrative volume and against a bounded page.
 */
export const AgentSessionAdminQuerySchema = z.object({
    createdAfter: z.iso.datetime().optional(),
    createdBefore: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    offset: z.coerce.number().int().min(0).default(0),
    sessionId: z.uuid().optional(),
    sortField: AgentSessionAdminSortFieldSchema.default('updated'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
    states: z
        .union([AgentSessionStateSchema, z.array(AgentSessionStateSchema).nonempty()])
        .transform((states) => (Array.isArray(states) ? states : [states]))
        .default(['Active']),
    title: z.string().trim().min(1).max(200).optional(),
    updatedAfter: z.iso.datetime().optional(),
    updatedBefore: z.iso.datetime().optional(),
    userId: z.uuid().optional(),
});
//# sourceMappingURL=adminSummary.js.map